// Which brand this page is being served as.
//
// ONE app, ONE bundle, ONE API, served at two names — see
// `docs/plan-address-book.md`, "Product shape: one system, two surfaces".
// socialtie.com and mysocialcal.com are ServerAliases on the same Apache vhost
// (`infra/apache-mysocialcal.conf`), so the only thing that differs at runtime
// is the hostname the browser asked for.
//
// This is deliberately NOT a user setting or a stored preference. The URL people
// typed is the whole signal: someone who went to socialtie.com wants the address
// book, and someone who went to mysocialcal.com wants their events. A toggle
// inside the app would be the "mode switch" that plan doc explicitly rules out.
//
// ⚠ Separate origin means separate `localStorage`, so a session on one name is
// NOT a session on the other. Same credentials, two logins. That is a known,
// accepted cost, not a bug — see the same doc's "concrete gotcha" section.

export type Brand = 'mysocialcal' | 'socialtie';

/**
 * Dev-only override, read from `localStorage`.
 *
 * There is no socialtie.com in development — the dev server answers on
 * `localhost:5173` for both brands — so without this there is no way to see or
 * test the SocialTie surface locally. Set `localStorage['msc.brand'] =
 * 'socialtie'` in the console and reload.
 *
 * Harmless in production: it only ever selects between two real brands, both of
 * which the same account can already reach by typing the other hostname.
 */
const OVERRIDE_KEY = 'msc.brand';

function override(): Brand | null {
  try {
    const v = localStorage.getItem(OVERRIDE_KEY);
    return v === 'socialtie' || v === 'mysocialcal' ? v : null;
  } catch {
    // Private mode, or a context where storage throws on access.
    return null;
  }
}

/**
 * The brand for the current page.
 *
 * Matched on the hostname rather than a full-origin equality test so that
 * `www.socialtie.com` and the `socialtie.localhost` dev vhost both work without
 * being listed one by one. `old.socialtie.com` is the PHP archive and never
 * serves this bundle, so it costs nothing that it would also match.
 */
export function brand(): Brand {
  return override() ?? (/(^|\.)socialtie\.(com|localhost)$/i.test(location.hostname)
    ? 'socialtie'
    : 'mysocialcal');
}

export function isSocialTie(): boolean {
  return brand() === 'socialtie';
}

/** Display name of the current brand, for headings and `alt` text. */
export function brandName(): string {
  return isSocialTie() ? 'SocialTie' : 'MySocialCal';
}

/** Path to the current brand's wordmark lockup in `web/public/`. */
export function brandLogo(): string {
  return isSocialTie() ? '/socialtie-logo.png' : '/logo.png';
}
